import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  // Check both old and new token keys for backward compatibility
  const token = localStorage.getItem('fx_token') || localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (name, email, password) => api.post('/auth/register', { name, email, password }),
  me: () => api.get('/auth/me'),
};

// Brokers API
export const brokersAPI = {
  getAll: (params) => api.get('/brokers', { params }),
  getFeatured: () => api.get('/brokers/featured'),
  getById: (id) => api.get(`/brokers/${id}`),
  getReviews: (id) => api.get(`/brokers/${id}/reviews`),
  createReview: (id, review) => api.post(`/brokers/${id}/reviews`, review),
  getStatsSummary: () => api.get('/brokers/stats/summary'),
  getStatsByCountry: () => api.get('/brokers/stats/by-country'),
  getStatsByRegulator: () => api.get('/brokers/stats/by-regulator'),
};

// News API
export const newsAPI = {
  getAll: (params) => api.get('/news', { params }),
  getFeatured: () => api.get('/news/featured'),
  getById: (id) => api.get(`/news/${id}`),
  // Live news from multiple sources
  getLiveMarketNews: (category = 'general') => api.get('/news/live/market', { params: { category } }),
  getLiveCompanyNews: (symbol, fromDate, toDate) => api.get(`/news/live/company/${symbol}`, { params: { from_date: fromDate, to_date: toDate } }),
  // Fast: read straight from our MongoDB cache (synced in background).
  // Falls back to legacy live endpoint if the DB hasn't seen the first sync yet.
  getCombinedNews: (category = 'all', country = 'us', limit = 50) =>
    api.get('/news/feed', { params: { category, limit } }).then(async (r) => {
      if (r.data?.news?.length) return r;
      // Empty DB → kick the slow live path so first-page-load isn't blank.
      return api.get('/news/live/combined', { params: { category, country, limit } });
    }),
  triggerNewsSync: () => api.post('/news/sync/now'),
  getBreakingNews: (limit = 10) => api.get('/news/live/breaking', { params: { limit } }),
};

// Calendar API
export const calendarAPI = {
  getEvents: (params) => api.get('/calendar', { params }),
};

// Toolbox API
export const toolboxAPI = {
  getAll: (params) => api.get('/toolbox', { params }),
  getById: (id) => api.get(`/toolbox/${id}`),
};

// Traders API
export const tradersAPI = {
  getAll: (params) => api.get('/traders', { params }),
  getById: (id) => api.get(`/traders/${id}`),
};

// Sparks API (Stories-style 24h ephemeral posts, max 34s video)
export const sparksAPI = {
  create: (payload) => api.post('/sparks', payload),
  getFeed: (limit = 50) => api.get('/sparks/feed', { params: { limit } }),
  getUserSparks: (userId) => api.get(`/sparks/user/${userId}`),
  getById: (sparkId) => api.get(`/sparks/${sparkId}`),
  view: (sparkId) => api.post(`/sparks/${sparkId}/view`),
  remove: (sparkId) => api.delete(`/sparks/${sparkId}`),
  hasActive: (userId) => api.get(`/sparks/has/${userId}`),
};

// Payments API
export const paymentsAPI = {
  createCheckout: (packageId, productId = null) => 
    api.post('/payments/checkout', {
      package_id: packageId,
      origin_url: window.location.origin,
      product_id: productId,
    }),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  getPackages: () => api.get('/payments/packages'),
};

// Stats API
export const statsAPI = {
  getStats: () => api.get('/stats'),
};

// Market Data API
export const marketAPI = {
  getHistory: (symbol, timeframe = '15m', periods = 200) => 
    api.get(`/market/history/${symbol}`, { params: { timeframe, periods } }),
  getQuote: (symbol) => api.get(`/market/quote/${symbol}`),
  getAllQuotes: () => api.get('/market/quotes'),
};

// WebSocket URL helper
export const getWebSocketURL = (path) => {
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  // Extract host from backend URL
  const url = new URL(backendUrl);
  return `${wsProtocol}//${url.host}${path}`;
};

// Seed API (for demo)
export const seedAPI = {
  seed: () => api.post('/seed'),
};

// Inquiries API (Contact, Career, Press)
export const inquiriesAPI = {
  submitContact: (data) => api.post('/inquiries/contact', data),
  submitCareer: (data) => api.post('/inquiries/career', data),
  submitPress: (data) => api.post('/inquiries/press', data),
  submitBrokerSetup: (data) => api.post('/inquiries/broker-setup', data),
  getAll: (params) => api.get('/inquiries', { params }),
  update: (id, data) => api.put(`/inquiries/${id}`, data),
  delete: (id) => api.delete(`/inquiries/${id}`),
};

// Careers API
export const careersAPI = {
  getPositions: () => api.get('/careers/positions'),
};

export default api;
